import { PHASE } from "../../../../common/constants.ts";
import type { Phase } from "../../../../common/types.ts";
import type { MobileNavigationEntry } from "../util/phaseNavigation.ts";

export type MarketHubAction = {
  labelKey: string;
  defaultLabel: string;
  // M3-4: a market action can navigate (path) or open the DraftPicksSheet in
  // place (sheet). The draft-picks page was retired into that overlay.
  path?: string[];
  sheet?: "draftPicks";
};

export type MarketHubPrimaryTask = {
  key: string;
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  descriptionParams?: Record<string, number | string>;
  primaryAction: MarketHubAction;
};

export type MarketHubState = {
  canSignFreeAgents: boolean;
  canTrade: boolean;
  hasPendingNegotiations: boolean;
  needsExpansionProtection: boolean;
};

export type TeamMarketContext = {
  capSpace: number;
  contractExpiringCount: number;
  freeAgentsAvailable: number;
  injuredPlayers: number;
  maxRosterSize: number;
  minRosterDeficit: number;
  minRosterSize: number;
  openRosterSpots: number;
  payroll: number;
  payrollLimit: number;
  pendingNegotiations: number;
  rosterCount: number;
  salaryCapType: string;
};

export type MarketHubResource = {
  key: string;
  labelKey: string;
  defaultLabel: string;
  value: string;
  valueKey?: string;
  valueParams?: Record<string, number | string>;
  detailKey: string;
  defaultDetail: string;
  detailParams?: Record<string, number | string>;
  status: "danger" | "warning" | "info" | "ok";
  path?: string[];
  // M3-4: a resource row can open the DraftPicksSheet in place instead of
  // navigating (the draft-picks page was retired into that overlay).
  sheet?: "draftPicks";
};

const task = (
  key: string,
  defaultTitle: string,
  defaultDescription: string,
  defaultLabel: string,
  target: { path: string[] } | { sheet: "draftPicks" },
  descriptionParams?: Record<string, number | string>,
): MarketHubPrimaryTask => ({
  defaultDescription,
  defaultTitle,
  descriptionKey: `marketHub.primary.${key}.description`,
  descriptionParams,
  key,
  primaryAction: {
    defaultLabel,
    labelKey: `marketHub.primary.${key}.action`,
    ...target,
  },
  titleKey: `marketHub.primary.${key}.title`,
});

const isDraftPhase = (phase: Phase) =>
  phase === PHASE.EXPANSION_DRAFT ||
  phase === PHASE.FANTASY_DRAFT ||
  phase === PHASE.DRAFT ||
  phase === PHASE.AFTER_DRAFT;

export const getMarketHubPrimaryTask = ({
  marketState,
  phase,
  recommendedEntry,
  teamMarketContext,
}: {
  marketState?: MarketHubState;
  phase: Phase;
  recommendedEntry?: MobileNavigationEntry;
  teamMarketContext?: TeamMarketContext;
}): MarketHubPrimaryTask | undefined => {
  if (marketState?.needsExpansionProtection) {
    return task(
      "protect",
      "Set your protection list",
      "Protect core players before the expansion draft can continue.",
      "Protect players",
      { path: ["protect_players"] },
    );
  }

  if (marketState?.hasPendingNegotiations || (teamMarketContext?.pendingNegotiations ?? 0) > 0) {
    return task(
      "negotiation",
      "Resolve contract talks",
      "{{count}} player(s) are waiting for a contract decision.",
      "Open negotiations",
      { path: ["negotiation"] },
      { count: teamMarketContext?.pendingNegotiations ?? 1 },
    );
  }

  if (marketState?.canSignFreeAgents && (teamMarketContext?.minRosterDeficit ?? 0) > 0) {
    return task(
      "rosterShort",
      "Fill the roster",
      "You are {{count}} player(s) below the minimum roster size.",
      "Browse free agents",
      { path: ["free_agents"] },
      { count: teamMarketContext?.minRosterDeficit ?? 0 },
    );
  }

  if (phase === PHASE.FREE_AGENCY) {
    return task(
      "freeAgency",
      "Use free agency",
      "Fill roster holes without breaking your budget.",
      "Browse free agents",
      { path: ["free_agents"] },
    );
  }

  if (isDraftPhase(phase) || phase === PHASE.DRAFT_LOTTERY) {
    return task(
      "draft",
      recommendedEntry?.defaultLabel ?? "Review the draft",
      recommendedEntry?.defaultDescription ?? "Use draft assets to improve the roster.",
      recommendedEntry?.defaultLabel ?? "Open draft",
      { path: recommendedEntry?.path ?? ["draft"] },
    );
  }

  if (marketState?.canTrade) {
    return task(
      "trade",
      "Improve through trade",
      "Use players and picks to address roster needs.",
      "Open trade",
      { path: ["trade"] },
    );
  }

  // M3-4: "review future assets" opens the DraftPicksSheet in place (the
  // draft-picks page was retired into that overlay) instead of navigating.
  return task(
    "assets",
    "Review future assets",
    "Transactions are limited, so review picks and future options.",
    "Open picks",
    { sheet: "draftPicks" },
  );
};

const resource = (
  key: string,
  defaultLabel: string,
  value: string,
  defaultDetail: string,
  status: MarketHubResource["status"],
  path?: string[],
  detailParams?: Record<string, number | string>,
): MarketHubResource => ({
  defaultDetail,
  defaultLabel,
  detailKey: `marketHub.resource.${key}.detail`,
  detailParams,
  key,
  labelKey: `marketHub.resource.${key}.label`,
  path,
  status,
  value,
});

const localizedValue = (
  marketResource: MarketHubResource,
  valueKey: string,
  valueParams?: Record<string, number | string>,
): MarketHubResource => ({
  ...marketResource,
  valueKey,
  valueParams,
});

export const getMarketHubResourceSummary = ({
  marketState,
  teamMarketContext,
}: {
  marketState?: MarketHubState;
  teamMarketContext?: TeamMarketContext;
}): MarketHubResource[] => {
  if (!teamMarketContext) {
    return [];
  }

  const statusResource =
    teamMarketContext.pendingNegotiations > 0
      ? resource(
          "pendingNegotiations",
          "Contract talks",
          String(teamMarketContext.pendingNegotiations),
          "Player decision(s) waiting.",
          "warning",
          ["negotiation"],
        )
      : marketState?.needsExpansionProtection
        ? localizedValue(
            resource(
              "protection",
              "Protection",
              "Open",
              "Expansion draft protection is required.",
              "danger",
              ["protect_players"],
            ),
            "marketHub.resourceValue.open",
          )
        : marketState?.canTrade
          ? localizedValue(
              resource(
                "tradeWindow",
                "Trade window",
                "Open",
                "Trades are available in this phase.",
                "ok",
                ["trade"],
              ),
              "marketHub.resourceValue.open",
            )
          : localizedValue(
              {
                ...resource(
                  "tradeClosed",
                  "Trade window",
                  "Closed",
                  "Trades are closed in this phase.",
                  "info",
                ),
                sheet: "draftPicks",
              },
              "marketHub.resourceValue.closed",
            );

  return [
    localizedValue(
      resource(
        teamMarketContext.minRosterDeficit > 0 ? "rosterShort" : "roster",
        "Roster",
        `${teamMarketContext.rosterCount} of ${teamMarketContext.maxRosterSize} players`,
        teamMarketContext.minRosterDeficit > 0
          ? "{{count}} short of the minimum."
          : "{{count}} open roster spot(s).",
        teamMarketContext.minRosterDeficit > 0 ? "danger" : "ok",
        ["roster"],
        {
          count:
            teamMarketContext.minRosterDeficit > 0
              ? teamMarketContext.minRosterDeficit
              : teamMarketContext.openRosterSpots,
        },
      ),
      "marketHub.resourceValue.rosterCount",
      {
        count: teamMarketContext.rosterCount,
        limit: teamMarketContext.maxRosterSize,
      },
    ),
    resource(
      teamMarketContext.capSpace >= 0 ? "budget" : "budgetOver",
      "Budget",
      `${Math.round(teamMarketContext.capSpace * 10) / 10}M`,
      teamMarketContext.capSpace >= 0
        ? "Available before the payroll limit."
        : "Payroll is above the current limit.",
      teamMarketContext.capSpace >= 0 ? "ok" : "warning",
      ["team_finances"],
    ),
    statusResource,
  ];
};
